import { ChangeEvent, Dispatch, SetStateAction } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'

import WithdrawInfoModal from 'components/WithdrawInfoModal'
import EmptyList from 'components/EmptyList'
import LinearLoader from 'components/LinearLoader'
import ScrollableBox from 'components/ScrollableBox'
import TableBodyRow from './TableBodyRow'
import TableHead from './TableHead'

import { useGetWithdrawalsByIdQuery } from 'services/operations'

import { useMediaSize } from 'hooks/useMediaSize'
import { Withdraw, WithdrawFilterParams, WithdrawPagingParams } from 'types/operation'

import s from '../OperationTable/styles'


const MIN_ROW_HEIGHT = 58
const FIRST_ROW_PADDING = 0

interface Props {
  filterParams: WithdrawFilterParams
  pagingParams: WithdrawPagingParams
  setPagingParams: (params: Partial<WithdrawPagingParams>) => void
  withdraw: Withdraw | null
  setWithdraw: Dispatch<SetStateAction<null | Withdraw>>
}

const WithdrawTable = ({
  filterParams,
  pagingParams,
  setPagingParams,
  withdraw,
  setWithdraw
}: Props) => {

  const { inputSize } = useMediaSize()

  const [searchParams] = useSearchParams()
  const wallet = searchParams.get('wallet')

  const { data, isFetching } = useGetWithdrawalsByIdQuery(
    {
      page: pagingParams.pageIndex,
      size: pagingParams.pageSize,
      filter: filterParams,
      walletId: wallet
    },
    { refetchOnMountOrArgChange: true }
  )

  const handlePageChange = (
    event: ChangeEvent<unknown>,
    value: number
  ) => {
    setPagingParams({ pageIndex: value - 1 })
  }

  return (
    <Paper elevation={0} sx={s.wrapper}>
      <ScrollableBox>
        <Table>
          <TableHead
            setPagingParams={setPagingParams}
            pagingParams={pagingParams}
          />
          <TableBody
            sx={{
              display: 'block',
              minHeight: `${
                MIN_ROW_HEIGHT * pagingParams.pageSize + FIRST_ROW_PADDING
              }px`,
            }}
          >
            {isFetching ? (
              <TableRow sx={s.loaderRow}>
                <TableCell sx={s.loaderCell}>
                  <LinearLoader />
                </TableCell>
              </TableRow>
            ) : !data?.content.length ? (
              <EmptyList />
            ) : (
              data.content.map((item) => (
                <TableBodyRow
                  key={item.id}
                  item={item}
                  setWithdraw={setWithdraw}
                />
              ))
            )}
          </TableBody>
        </Table>
      </ScrollableBox>
      <Box sx={s.paginationWrapper}>
        {!!data?.totalPages && data?.totalPages > 1 && (
          <Pagination
            page={pagingParams.pageIndex + 1}
            onChange={handlePageChange}
            size={inputSize}
            count={data?.totalPages}
            color='primary'
            shape='rounded'
            showFirstButton
            showLastButton
          />
        )}
      </Box>
      {withdraw && <WithdrawInfoModal withdraw={withdraw} onClose={() => setWithdraw(null)}/>}
    </Paper>
  )
}

export default WithdrawTable